import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { debounce } from '../../../src/utils/helpers';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VesselPositionModel } from '../../../src/models/vessel-position-model';
import '../../voyage-tooltip/src/voyage-tooltip';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';

const MAX_SUGGESTIONS = 10;
export const gpsCoordinateText : Suggestion[] = [{ text: `Position input examples:`}, 
                                                 { text: `Input 07-50.500, 001-30.725 = 07°50.500 N ; 001°30.725 E`},
                                                 { text: `Input -27-30.225, -010-20.700 = 27°30.225 S ; 010°20.700 W`}];

interface Suggestion
{
    text: string;
    selectable?: boolean;
}

export interface PositionOrTerminal
{
    longitude?: number;
    latitude?: number;
    terminal?: string;
}

@customElement('voyage-location-input')
export class VoyageLocationInput extends LitElement
{
    @property({type: Boolean}) invalid: boolean = true;
    @property({type: String}) label: string = "";
    @property({type: Boolean, attribute: "include-terminals"}) includeTerminals: boolean = false;
    @property({type: Boolean, attribute: "show-current-position"}) showCurrentPosition: boolean = false;
    @property({attribute: false}) allowEmptyInput: boolean = false;

    constructor()
    {
        super();

        this._populateTerminalNames = this._populateTerminalNames.bind(this);
        this._onInput = this._onInput.bind(this);
        this._currentPositionClick = this._currentPositionClick.bind(this);
        this.addEventListener('focusin', () => this._hasFocus = true );
        this.addEventListener('focusout', () => this._hasFocus = false );
    }

    @property({type: String})
    get value()
    {
        return this._value;
    }

    set value(value)
    {
        this._value = value;
        if (this._input && this._input.value !== this._value)
        {
            this._input.value = this._value;
        }

        if (!this.includeTerminals)
        {
            this._suggestions = [...gpsCoordinateText];
        }
        else if (this._value === "")
        {
            this._suggestions = [{ text: "Start typing in a Port/Terminal name or a GPS coordinate" }];
        }
        else if (/[^\-0-9.,]/.test(this._value))
        {
            // Text has been entered, search for matching terminal names
            const matches = this._searchTerminals(this._value);

            this._suggestions = [];
            for (let i = 0; i < Math.min(matches.length, MAX_SUGGESTIONS); i++)
            {
                this._suggestions.push({ text: matches[i], selectable: true });
            }

            if (matches.length > MAX_SUGGESTIONS)
            {
                this._suggestions = [...this._suggestions, ...gpsCoordinateText];
            }
        }
        else    
        {
            // Only numbers has been entered, assume position
            this._suggestions = [{ text: "GPS coordinate (xx xx xx, yyy yy yy)" }];
        }

        this._checkValidity();
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                font-family: var(--component-paragraph-ff, serif);
                --tooltip-background-color: var(--primary-blue-01, blue);
                width: 400px;
            }

            .container {
                display: flex;
                flex-direction: column;
                font-size: 14px;
                color: var(--neutrals-grey-02);
                width: 100%;
            }

            .inputContainer {
                position: relative;
                display: flex;
                align-items: center;
                box-sizing: border-box;
                width: 100%;
                height: 52px;
                border: 1px solid var(--neutrals-grey-06);
                border-radius: 3px;
                background-color: var(--neutrals-grey-10, white);
            }

            .inputContainer:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
            }

            .inputContainer[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }

            label {
                margin-bottom: 8px;
            }

            button {
                padding: 0;
                width: fit-content;
                height: fit-content;
                border: none;
                background: none;
            }

            button:focus {
                outline: none;
            }

            input {
                font-family: var(--component-paragraph-ff, serif);
                font-size: 14px;
                box-sizing: border-box;
                width: 100%;
                padding-left: 10px;
                padding-right: 40px;
                border: none;
                outline: none;
                color: var(--neutrals-grey-02);
                background-color: unset;
            }

            #currentPosition {
                fill: var(--neutrals-grey-02, black);
                position: absolute;
                right: 16px;
            }

            #currentPosition > svg {
                display: block;
                width: 16px;
                height: 16px;
            }

            .explainer {
                font-size: 12px;
                color: var(--neutrals-grey-05);
            }

            .suggestionsPositioner {
                position: relative;
                height: 0;
                width: 100%;
            }

            .suggestions {
                position: absolute;
                z-index: 3;
                display: flex;
                flex-direction: column;
                background-color: var(--neutrals-grey-10, white);
                border: 1px solid var(--neutrals-grey-06, black);
                box-shadow: 5px 5px 10px rgba(0,0,0,0.5);
                border-radius: 3px;
            }

            .suggestionText {
                margin: 5px;
            }

            .suggestionButton {
                margin: 5px;
                text-align: left;
                font-family: var(--component-paragraph-ff, serif);
                font-size: 14px;
                color: var(--neutrals-grey-02);
            }

            .suggestionButton:hover {
                background-color: var(--neutrals-grey-08);
            }

            .suggestionButton:focus {
                background-color: var(--neutrals-grey-08);
            }

            .tooltip {
                white-space: nowrap;
                margin: 6px;
                color: var(neutrals-grey-10, white);
            }

            voyage-tooltip {
                z-index: 3;
            }
        `];
    }

    render()
    {
        return html`
            <div class="container">
                <label for="fromInput">${this.label}</label>
                <div class="inputContainer" ?invalid=${this.invalid}>
                    <input id="fromInput" type="text">
                    <button id="currentPosition" ?hidden=${!this.showCurrentPosition} @click=${this._currentPositionClick}>${SvgIcons.location}</button>
                </div>
                ${this._hasFocus? html`
                <div class="suggestionsPositioner">
                    <div class="suggestions">
                    ${this._suggestions.map( suggestion => {
                        if (suggestion.selectable)
                        {
                            return html`<button class="suggestionButton" @click=${() => this._suggestionClick(suggestion.text)}>${suggestion.text}</button>`;
                        }
                        else
                        {
                            return html`<div class="suggestionText">${suggestion.text}</div>`;
                        }
                    })}
                    </div>
                </div>
                ` : html``}
                <div class="explainer">Type in ${this.includeTerminals ? "a Port/Terminal name or ": ""}a GPS coordinate</div>
            </div>
            <voyage-tooltip .for=${this._currentPositionButton} .positionPriority=${['right']}><span class="tooltip">Use vessel current location</span></voyage-tooltip>
        `;
    }

    connectedCallback()
    {
        super.connectedCallback();
        this._populateTerminalNames();
        TerminalsModel!.addEventListener('terminals-updated', this._populateTerminalNames);
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();
        TerminalsModel!.removeEventListener('terminals-updated', this._populateTerminalNames);
    }

    firstUpdated()
    {
        const debounceDelay = 250;
        this._input = <HTMLInputElement>this.shadowRoot!.getElementById('fromInput');
        this._input.addEventListener('input', debounce(this._onInput, debounceDelay));

        this._currentPositionButton = this.shadowRoot!.getElementById("currentPosition");

        this._checkValidity();
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("allowEmptyInput")) {
            this._checkValidity();
        }
        
        super.updated(changedProperties);
    }

    @property({attribute: false}) private _suggestions: Suggestion[] = [];
    @property({attribute: false}) private _hasFocus: boolean = false;
    @property({attribute: false}) private _currentPositionButton: HTMLElement|null = null;
    private _value: string = "";
    private _input: HTMLInputElement|null = null;
    private _terminalNames: string[] = [];
    private _terminalMap: Map<string,string> = new Map();

    // -178 12 48, 83 32 17.23
    private _positionRegex = /(-?[0-9]{1,2})-([0-9]{1,2}[.][0-9][0-9][0-9])[ ]?[,][ ]?(-?[0-9]{1,3})-([0-9]{1,2}[.][0-9][0-9][0-9])/;
    // 121° 23.234′ N, 23° 23,23′ W  .. Degree and Minute characters are optional
    private _positionGPSRegex = /([0-9]{1,3})[°]? ([0-9]{1,2}[.|,][0-9][0-9][0-9])[′]? ([N,S])[ ]?[,][ ]?([0-9]{1,3})[°]? ([0-9]{1,2}[.|,][0-9][0-9][0-9])[′]? ([E,W])/;
    // 31° 50.232′ N, 34° 38.234′ E
    private _positionMarineRegex = /([0-9]{1,3})[°] ([0-9]{1,2})[′] ([0-9]{1,2}[.|,][0-9][0-9][0-9])[″] ([N,S])[ ]?[,][ ]?([0-9]{1,3})[°] ([0-9]{1,2})[′] ([0-9]{1,2}[.|,][0-9][0-9][0-9])[″] ([E,W])/;

    private _suggestionClick(terminalName: string)
    {
        this.value = terminalName;
        this.blur();
        this.dispatchEvent(new Event("value-changed"));
    }

    private _onInput()
    {
        this.value = this._input!.value;
        this._checkValidity();
        this.dispatchEvent(new Event("value-changed"));
    }

    private _gpsFormatFromDecimalCoords(lon: number, lat: number) {
        const position = CoordinatesUtils.decimalToDegreesAndMinutes([lon, lat]);
        return `${position.lat}, ${position.lon}`;
    }

    private _currentPositionClick()
    {
        this.value = this._gpsFormatFromDecimalCoords(VesselPositionModel!.position!.longitude!, VesselPositionModel!.position!.latitude!);
        this.dispatchEvent(new Event("value-changed"));
        this.dispatchEvent(new Event("current-position-click"));
    }

    private _searchTerminals(value: string)
    {
        const valueLowerCase = value.toLowerCase();
        const matchingTerminals = this._terminalNames.filter( terminalName => terminalName.toLowerCase().includes(valueLowerCase) );
        return matchingTerminals;
    }

    private _populateTerminalNames()
    {
        this._terminalNames = [];
        for (const [_, value] of TerminalsModel!.terminals)
        {
            const text = `${value.city} (${value.location})`;
            this._terminalNames.push(text);
            this._terminalMap.set(text, value.locationId);
        }
    }

    private _checkValidity()
    {
        if (!this._value && this.allowEmptyInput) {
            this.invalid = false;
            return;
        }
        
        if (this._positionMarineRegex.test(this._value)) {
            this.invalid = false;
            // convert to the gps format.
            const coords = this._decimalCoordsFromMarineFormat(this._value);
            this.value = this._gpsFormatFromDecimalCoords(coords!.longitude, coords!.latitude);
        } else  if (this._positionRegex.test(this._value)) {
            this.invalid = false;
            // convert to the gps format.
            const coords = this._decimalCoordsFromPositionFormat(this._value);
            this.value = this._gpsFormatFromDecimalCoords(coords!.longitude, coords!.latitude);
        } else if (this._positionGPSRegex.test(this._value)) {
            this.invalid = false;
            const coords = this._decimalCoordsFromGPSFormat(this._value);
            // making sure the degrees and minutes characters are there.
            let coordsInGPSFormat = this._gpsFormatFromDecimalCoords(coords!.longitude, coords!.latitude);
            if (coordsInGPSFormat != this._value) {
                this.value = coordsInGPSFormat;
            }
        } else if (this._terminalMap.has(this._value) && this.includeTerminals) {
            this.invalid = false;
        } else {
            this.invalid = true;
        }
    }

    setFromTerminalIdOrPosition(value: PositionOrTerminal)
    {
        if (value && value.longitude !== undefined && value.latitude !== undefined && value.terminal)
        {
            // Use terminal name *if* it matches the position
            const terminal = TerminalsModel!.getTerminal(value.terminal);
            if (terminal && terminal.longitude === value.longitude && terminal.latitude === value.latitude)
            {
                this.value = `${terminal.city} (${terminal.location})`;
            }
            else
            {
                this.value = this._gpsFormatFromDecimalCoords(value.longitude, value.latitude);
            }
        }
        else if (value && value.longitude !== undefined && value.latitude !== undefined)
        {
            this.value = this._gpsFormatFromDecimalCoords(value.longitude, value.latitude);
        }
        else if (value && value.terminal)
        {
            const terminal = TerminalsModel!.getTerminal(value.terminal);
            if (terminal)
            {
                this.value = `${terminal.city} (${terminal.location})`;
            }
            else
            {
                console.warn(`Terminal ${value.terminal} not found!`);
                this.value = "";
            }
        }
        else
        {
            this.value = "";
        }
    }

    private _decimalCoordsFromMarineFormat(value: string) {
        const regexResult = this._positionMarineRegex.exec(value);
        if (!regexResult)
        {
            return null;
        }

        const lonSign = regexResult[8] === "E" ? 1 : -1;
        const lon = lonSign * CoordinatesUtils.degreesToDecimal(`${regexResult[5]} ${regexResult[6]} ${regexResult[7]}`);
        const latSign = regexResult[4] === "N" ? 1 : -1;
        const lat = latSign * CoordinatesUtils.degreesToDecimal(`${regexResult[1]} ${regexResult[2]} ${regexResult[3]}`);
    
        return {
            longitude: lon,
            latitude: lat
        }
    }

    private _decimalCoordsFromPositionFormat(value: string) {
        const regexResult = this._positionRegex.exec(value);
        if (!regexResult) {
            return null;
        }

        const lat = CoordinatesUtils.degreesToDecimal(`${regexResult[1]} ${Math.floor(parseFloat(regexResult[2]))} ${(parseFloat(regexResult[2]) - Math.floor(parseFloat(regexResult[2])))*60}`);
        const lon = CoordinatesUtils.degreesToDecimal(`${regexResult[3]} ${Math.floor(parseFloat(regexResult[4]))} ${(parseFloat(regexResult[4]) - Math.floor(parseFloat(regexResult[4])))*60}`);

        return {
            longitude: lon,
            latitude: lat
        }
    }

    private _decimalCoordsFromGPSFormat(value: string) {
        const regexResult = this._positionGPSRegex.exec(value);
        if (!regexResult) {
            return null;
        }

        const lat = CoordinatesUtils.degreesToDecimal(`${regexResult[1]} ${regexResult[2]} ${regexResult[3]}`);
        const lon = CoordinatesUtils.degreesToDecimal(`${regexResult[4]} ${regexResult[5]} ${regexResult[6]}`);

        return {
            longitude: lon,
            latitude: lat
        }
    }

    getPosition()
    {
        if (this.invalid)
        {
            return null;
        }

        if (this._terminalMap.has(this._value))
        {
            const terminal = TerminalsModel!.getTerminal(this._terminalMap.get(this._value)!);
            return {
                longitude: terminal!.longitude,
                latitude: terminal!.latitude
            }
        }

        if (this._positionMarineRegex.test(this._value))
        {
            return this._decimalCoordsFromMarineFormat(this.value);
        }
        
        if (this._positionRegex.test(this._value))
        {
            return this._decimalCoordsFromPositionFormat(this.value);
        }

        if (this._positionGPSRegex.test(this._value))
        {
            return this._decimalCoordsFromGPSFormat(this.value);
        }
    }

    getTerminalId()
    {
        return this._terminalMap.get(this._value);
    }
}
